<template>
  <div>
    <div class="pt-3 px-3 pb-2">
      <ApplicationCard
        v-for="application in applications"
        :key="application.id"
        :application="application"
        @click="$emit('clickOrderCard', application.id)"
        class="mb-2"
      />

      <div v-if="isInitialDataFetched" class="infinite-loading-container">
        <p
          v-if="applications.length === 0"
          class="w-100 p-3 bg-white text-14 text-center"
        >
          お仕事はありません
        </p>
        <p v-else-if="!hasNext" class="my-3 text-size-14">
          ここが最後です
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Watch, Vue } from "vue-facing-decorator";
import { RouteLocationNormalizedLoaded } from "vue-router";
import ApplicationCard from "@/components/organisms/applications/ApplicationCard.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { showErrorModal, ErrorType } from "@/utils/showErrorModal";
import { Application } from "@/types/order/Application";
import applicationStatus from "@/models/applicationStatus";
import applicationTab from "@/models/applicationTab";

const FETCH_APPLICATIONS_COUNT = 15;

@Component({
  components: {
    ApplicationCard
  }
})
export default class ApplicationsClosedList extends Vue {
  applications: Application[] = [];

  // InfinityScrollの制御
  isInfinityScrollLoading = false;
  hasNext = false;
  isInitialDataFetched = false;

  get isEvaluatedPage() {
    return this.$route.query.tab === applicationTab.EVALUATED;
  }
  get isCanceledPage() {
    return this.$route.query.tab === applicationTab.CANCELED;
  }

  @Watch("$route", { immediate: true })
  onChangeRoute(newVal: RouteLocationNormalizedLoaded) {
    // 自ページおよび完了・キャンセルタブへの遷移時以外は何もしない
    // NOTE: 現状、このコンポーネントは ApplicationsList でのみ利用されているためこのようにしているが、
    // 本来は views 以下で制御すべき
    if (
      newVal.name !== "ApplicationsList" ||
      !(this.isEvaluatedPage || this.isCanceledPage)
    ) {
      return;
    }
    this.init();
  }

  async init() {
    this.applications = [];
    this.isInitialDataFetched = false;
    try {
      this.$loading.show();
      await this.fetchApplications();
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
      this.isInitialDataFetched = true;
    }
  }

  mounted() {
    window.addEventListener("scroll", this.onScroll);
  }

  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  }

  // 他タブと違い件数が多くなりInfiniteLoadingを利用するためstoreからではなくAPIから個別に取得する
  async onScroll() {
    if (!this.hasNext) {
      return;
    }

    // 一番下までスクロールしたら次のデータを取得する
    if (
      !this.isInfinityScrollLoading &&
      Math.ceil(window.innerHeight + window.scrollY) >= // androidは小数点が入るがoffsetHeightは整数なのでceilする
        document.body.offsetHeight
    ) {
      try {
        this.isInfinityScrollLoading = true;
        this.$loading.show();
        await this.fetchApplications();
      } catch (error) {
        await showErrorModal(this, error as ErrorType);
      } finally {
        this.isInfinityScrollLoading = false;
        this.$loading.hide();
      }
    }
  }

  async fetchApplications() {
    const statuses: string[] = [];
    if (this.isEvaluatedPage) {
      statuses.push(applicationStatus.EVALUATED);
    } else if (this.isCanceledPage) {
      statuses.push(applicationStatus.CANCELED);
      statuses.push(applicationStatus.REQESTED_CANCEL);
    }

    const {
      results: applications,
      next
    } = await applicationService.fetchApplications({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      status__in: statuses.join(","),
      ordering: "-applied_start_work_at",
      offset: this.applications.length,
      limit: FETCH_APPLICATIONS_COUNT
    });
    if (applications.length) {
      this.applications = [...this.applications, ...applications];
    }
    this.hasNext = !!next;
  }
}
</script>

<style lang="scss" scoped>
.infinite-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
